interface OrganizationSchema {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  description: string;
  sameAs?: string[];
}

interface WebSiteSchema {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  description: string;
  potentialAction: {
    '@type': string;
    target: {
      '@type': string;
      urlTemplate: string;
    };
    'query-input': string;
  };
}

interface BreadcrumbItemSchema {
  '@type': string;
  position: number;
  name: string;
  item: string;
}

interface BreadcrumbListSchema {
  '@context': string;
  '@type': string;
  itemListElement: BreadcrumbItemSchema[];
}

interface WebPageSchema {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  url: string;
  breadcrumb?: {
    '@type': string;
    itemListElement: BreadcrumbItemSchema[];
  };
}

interface FAQItem {
  '@type': string;
  name: string;
  acceptedAnswer: {
    '@type': string;
    text: string;
  };
}

interface FAQPageSchema {
  '@context': string;
  '@type': string;
  mainEntity: FAQItem[];
}

const baseUrl = 'https://www.beiwomovie.com';

export const organizationSchema: OrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '被窝网电影大全',
  url: baseUrl,
  description: '被窝网电影大全为您提供海量免费高清电影在线观看，聚合热门电影电视剧资源，最新全集不卡顿流畅播放，尽享舒适观影。',
  sameAs: [
    'https://weibo.com/beiwomovie',
  ],
};

export const webSiteSchema: WebSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '被窝网电影大全',
  url: baseUrl,
  description: '被窝网电影大全为您提供海量免费高清电影在线观看，聚合热门电影电视剧资源。',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${baseUrl}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

export function getWebPageSchema(
  name: string,
  description: string,
  url: string,
  breadcrumbItems?: { name: string; url: string }[]
): WebPageSchema {
  const schema: WebPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${baseUrl}${url}`,
  };
  if (breadcrumbItems && breadcrumbItems.length > 0) {
    schema.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem' as const,
        position: index + 1,
        name: item.name,
        item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
      })),
    };
  }
  return schema;
}

export function getBreadcrumbSchema(
  items: { name: string; url: string }[]
): BreadcrumbListSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
    })),
  };
}

export const faqPageSchema: FAQPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '被窝网电影的影视资源需要付费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不需要！被窝网电影大全所有影视资源完全免费，无需注册登录，无需任何付费，随时随地免费在线观看高清电影和电视剧。',
      },
    },
    {
      '@type': 'Question',
      name: '观看电影会卡顿缓冲吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '被窝网电影采用高速服务器和CDN加速技术，确保播放流畅不缓冲。只要您的网络正常，即可享受流畅的观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '被窝网电影支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '被窝网电影完美支持电脑、平板和手机等多种设备访问。无论是在家使用电脑，还是外出使用手机，都能流畅观看高清影视资源。',
      },
    },
    {
      '@type': 'Question',
      name: '影视资源更新速度快吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '被窝网电影资源更新速度非常快，新上映的电影和热播电视剧会在第一时间更新上线，让您不错过任何精彩内容。',
      },
    },
    {
      '@type': 'Question',
      name: '如何找到想看的电影？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过被窝网电影首页的分类导航按类型浏览，也可以使用搜索功能直接查找。我们提供动作片、喜剧片、爱情片、科幻片、恐怖片、悬疑片、电视剧等十余种分类，方便您快速定位。',
      },
    },
  ],
};

export default function SchemaOrg({ schema }: { schema: Record<string, unknown> }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}